import { useState } from "react";
import { useTrackingProducts } from "@/hooks/useTrackingProducts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCurrency } from "@/lib/utils";
import { ShoppingCart, Package, Check, Plus, Minus } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "sonner";
import { useCart } from "@/contexts/CartContext";

interface ProductsSelectionProps {
  productType: 'gps_tracker';
}

export function ProductsSelection({ productType }: ProductsSelectionProps) {
  const { activeCompany } = useAuth();
  const { data: products = [], isLoading } = useTrackingProducts('gps_device');
  const { addItem, isInCart } = useCart();
  
  const [quantities, setQuantities] = useState<Record<string, number>>({});

  const getQuantity = (productId: string) => quantities[productId] ?? 1;
  const setQuantity = (productId: string, qty: number) => {
    setQuantities(prev => ({ ...prev, [productId]: Math.max(1, qty) }));
  };

  const handleAddToCart = (product: any) => {
    if (!activeCompany?.id) {
      toast.error('Empresa não identificada');
      return;
    }

    const quantity = getQuantity(product.id);

    addItem({
      type: 'gps_tracker',
      productId: product.id,
      productName: product.name,
      description: product.description || '',
      quantity,
      unitPrice: product.price_cents,
    });

    toast.success('Produto adicionado ao carrinho!', {
      description: `${quantity}x ${product.name} adicionado com sucesso.`,
    });
  };

  if (isLoading) {
    return <div className="text-center py-8 text-muted-foreground">Carregando produtos...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="text-center space-y-2">
        <h3 className="text-2xl font-bold">Rastreadores GPS</h3>
        <p className="text-muted-foreground">Dispositivos de rastreamento veicular em tempo real</p>
        <Badge variant="outline" className="text-xs">
          🛒 Adicione ao carrinho e finalize sua compra
        </Badge>
      </div>
      
      {products.length === 0 ? (
        <div className="text-center py-12 space-y-4">
          <Package className="h-16 w-16 mx-auto text-muted-foreground opacity-50" />
          <div>
            <h3 className="text-lg font-semibold">Nenhum produto disponível</h3>
            <p className="text-muted-foreground text-sm mt-2">
              Os produtos de Rastreadores GPS serão listados aqui quando estiverem disponíveis.
            </p>
          </div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => {
            const quantity = getQuantity(product.id);
            return (
              <Card key={product.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle>{product.name}</CardTitle>
                  <CardDescription>{product.description}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-2xl font-bold text-primary">
                    {formatCurrency(product.price_cents / 100)}
                  </div>

                  {/* Seletor de Quantidade */}
                  <div className="space-y-2">
                    <Label className="text-sm">Quantidade</Label>
                    <div className="flex items-center gap-2">
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => setQuantity(product.id, quantity - 1)}
                        disabled={quantity <= 1}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <Input
                        type="number"
                        min="1"
                        value={quantity}
                        onChange={(e) => setQuantity(product.id, parseInt(e.target.value) || 1)}
                        className="w-20 text-center"
                      />
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => setQuantity(product.id, quantity + 1)}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => handleAddToCart(product)}
                    disabled={isInCart(product.id)}
                  >
                    {isInCart(product.id) ? (
                      <>
                        <Check className="h-4 w-4 mr-2" />
                        No Carrinho
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4 mr-2" />
                        Adicionar ao Carrinho
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
